import {
  ADMIN_BRAND_DETAILS,
  ADMIN_LOCATIONS_DETAILS,
  ADMIN_USERS_DETAILS,
  Disclaimer,
  DuplicateStoreConnectionMeta,
  lightTheme,
  noImage,
  Pill,
  renderProviderIcon,
  renderProviderName,
  SystemProblem,
  TextButton,
  useSafeWindowOpen,
} from '@vgrubs/components';
import { SOURCE_LABEL } from '../helpers/systemProblemLabels';
import {
  Branch,
  BrandActions,
  BrandName,
  BrandNode,
  BrandSide,
  BrandText,
  ConnectionTone,
  Eyebrow,
  Muted,
  ByLine,
  SourceTag,
  StoreHeader,
  StoreId,
  StoreLogo,
  StoreText,
  StoreTitle,
  Tree,
} from './ConnectionMap.styled';

/**
 * Each side's state as a pill, drawn the way Manage Providers draws a
 * provider's status (ProviderStatusComponent): white words on a solid fill.
 * Red is the conflict, green the brand that rightly has the store, grey the
 * one vGrubs unhooked. A problem resolved by hand gets none - nothing records
 * which side went.
 */
const statePill = (tone: ConnectionTone) =>
  ({
    live: { label: 'Still connected', color: lightTheme.primary },
    kept: { label: 'Connected', color: lightTheme.green },
    cut: { label: 'Disconnected', color: lightTheme.lightGray },
    past: null,
  }[tone]);

/**
 * How each side's connection stands. Only known for certain while the problem
 * is open, or when vGrubs did the disconnecting itself: once somebody resolves
 * it, one side went by hand and nothing records which.
 */
const tones = (
  problem: SystemProblem,
  meta: DuplicateStoreConnectionMeta
): { newer: ConnectionTone; older: ConnectionTone } => {
  if (meta.action === 'disconnected') return { newer: 'cut', older: 'kept' };
  if (problem.status === 'open') return { newer: 'live', older: 'live' };
  return { newer: 'past', older: 'past' };
};

type BrandProps = {
  role: string;
  tone: ConnectionTone;
  brandId: number | null;
  brandName: string | null;
  locationId: number | null;
  locationName: string | null;
  /** Its own provider, when it is the store's POS twin (grubhubPos, ...). */
  asProvider?: string | null;
  /** Who connected it, by name. Only the problem itself carries one. */
  connectedByName?: string | null;
  /** By id - all a conflict carries, so it gets a link instead of a name. */
  connectedById?: number | null;
  open: (route: string, id: number) => void;
};

const Brand = ({
  role,
  tone,
  brandId,
  brandName,
  locationId,
  locationName,
  asProvider,
  connectedByName,
  connectedById,
  open,
}: BrandProps) => {
  const state = statePill(tone);
  const showUser = () =>
    connectedById != null && open(ADMIN_USERS_DETAILS, connectedById);

  return (
    <BrandNode>
      <BrandText>
        <Eyebrow>{role}</Eyebrow>
        <BrandName>{brandName ?? 'Unknown brand'}</BrandName>
        {/* A single-brand restaurant's brand is usually named after the
            location; saying it twice reads as a glitch. */}
        {locationName && locationName !== brandName && (
          <Muted>at {locationName}</Muted>
        )}
        {asProvider && <Muted>as {renderProviderName(asProvider)}</Muted>}

        {/* The same small underlined TextButton as View Location / View
            Brand, so the three links read as one kind of thing. */}
        {connectedByName && connectedById == null && (
          <Muted>by {connectedByName}</Muted>
        )}
        {connectedById != null && (
          <ByLine>
            by
            <TextButton
              color={lightTheme.primary}
              $underline
              onClick={showUser}
            >
              {connectedByName ?? `user #${connectedById}`}
            </TextButton>
          </ByLine>
        )}
      </BrandText>

      <BrandSide>
        {state && (
          <Pill $background={state.color}>
            <Disclaimer $m="0" $color={lightTheme.white}>
              {state.label}
            </Disclaimer>
          </Pill>
        )}
        <BrandActions>
          {locationId !== null && (
            <TextButton
              color={lightTheme.primary}
              $underline
              onClick={() => open(ADMIN_LOCATIONS_DETAILS, locationId)}
            >
              View Location
            </TextButton>
          )}
          {brandId !== null && (
            <TextButton
              color={lightTheme.primary}
              $underline
              onClick={() => open(ADMIN_BRAND_DETAILS, brandId)}
            >
              View Brand
            </TextButton>
          )}
        </BrandActions>
      </BrandSide>
    </BrandNode>
  );
};

/**
 * One store and the brands it ended up on, drawn as what it is: the store on
 * top, the brand that connected last and the brand(s) that already had it as
 * rows under it, each with a pill saying where that connection stands. Every link opens a new tab - the problem is what support is working
 * from.
 */
export const ConnectionMap = ({
  problem,
  meta,
}: {
  problem: SystemProblem;
  meta: DuplicateStoreConnectionMeta;
}) => {
  const openInNewTab = useSafeWindowOpen();
  const open = (route: string, id: number) =>
    openInNewTab(route.replace(':id', String(id)), { immediate: true });
  const { newer, older } = tones(problem, meta);

  return (
    <Tree>
      <StoreHeader>
        <StoreLogo>
          <img alt="" src={renderProviderIcon(meta.provider, noImage)} />
        </StoreLogo>
        <StoreText>
          <StoreTitle>
            {renderProviderName(meta.provider)}
            <SourceTag>{SOURCE_LABEL[meta.source] ?? meta.source}</SourceTag>
          </StoreTitle>
          <StoreId>Store {meta.storeId}</StoreId>
        </StoreText>
      </StoreHeader>

      <Branch>
        <Brand
          role="Connected last"
          tone={newer}
          brandId={problem.isLocation ? null : problem.brandId}
          brandName={problem.brandName}
          locationId={problem.locationId}
          locationName={problem.locationName}
          connectedByName={problem.relatedUserName}
          connectedById={problem.relatedUserId}
          open={open}
        />
      </Branch>

      {meta.conflicts.map((conflict) => (
        <Branch key={conflict.providerId}>
          <Brand
            role="Already had it"
            tone={older}
            brandId={conflict.brandId}
            brandName={conflict.brandName}
            locationId={conflict.locationId}
            locationName={conflict.locationName}
            asProvider={
              conflict.provider !== meta.provider ? conflict.provider : null
            }
            connectedById={conflict.connectedById}
            open={open}
          />
        </Branch>
      ))}
    </Tree>
  );
};
